// Parser telemetry — writes one row per parse attempt to `parser_events`.
//
// STRICTLY fire-and-forget: every DB call is wrapped in try/catch, failures are
// logged with console.warn, and nothing here may block, delay, or fail the
// parse UX. Callers should NOT await these in the hot path (kick them off and
// keep the returned promise for the row id if the Confirm/Dispute UI needs it).
//
// The `parser_events` table and its RLS policies already exist in production.
// This module never creates or alters schema.

import { supabase } from '../supabase';
import type { PlausibilityFlag } from './plausibilityValidator';
import { computeFormatFingerprint } from './formatFingerprint';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type ParseStatus = 'success' | 'manual_required' | 'error';

export interface ParserEventPayload {
  project_id: string | null;
  utility_name: string | null;
  file_name: string;
  file_size_bytes: number | null;
  file_type: string | null;
  sheet_count: number | null;
  chosen_sheet: string | null;
  header_row_index: number | null;
  header_confidence: number | null;
  timestamp_column: string | null;
  demand_column: string | null;
  demand_unit: string | null;
  interval_minutes: number | null;
  row_count: number | null;
  expected_row_count: number | null;
  span_days: number | null;
  peak_kw: number | null;
  parse_status: ParseStatus;
  flags: PlausibilityFlag[];
  // Format-fingerprint inputs (present whenever profiling ran). The hash itself
  // is computed here so callers never touch crypto; null inputs → null hash.
  fingerprintHeader?: (string | null)[] | null;
  fingerprintColumnKinds?: string[] | null;
}

/**
 * Insert one telemetry row. Returns the new row id (so Confirm/Dispute can
 * update user_action later), or null if telemetry failed for any reason.
 * Never throws.
 */
export async function logParserEvent(payload: ParserEventPayload): Promise<string | null> {
  try {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData?.user?.id ?? null;
    if (!userId) {
      // RLS requires an authenticated user to insert own rows; skip quietly.
      return null;
    }

    // Any severity-verify flag means we are asking the user to confirm.
    const hasVerify = payload.flags.some(f => f.severity === 'verify');

    // project_id is a uuid FK — never send a local/optimistic id.
    const projectId =
      payload.project_id && UUID_RE.test(payload.project_id) ? payload.project_id : null;

    // Format fingerprint — SHA-256 over normalized header + column kinds. Never
    // blocks the insert; null when profiling did not run.
    const format_fingerprint = await computeFormatFingerprint(
      payload.fingerprintHeader ?? null,
      payload.fingerprintColumnKinds ?? null,
    );

    const { data, error } = await supabase
      .from('parser_events')
      .insert({
        user_id: userId,
        project_id: projectId,
        utility_name: payload.utility_name,
        file_name: payload.file_name,
        file_size_bytes: payload.file_size_bytes,
        file_type: payload.file_type,
        sheet_count: payload.sheet_count,
        chosen_sheet: payload.chosen_sheet,
        header_row_index: payload.header_row_index,
        header_confidence: payload.header_confidence,
        timestamp_column: payload.timestamp_column,
        demand_column: payload.demand_column,
        demand_unit: payload.demand_unit,
        interval_minutes: payload.interval_minutes,
        row_count: payload.row_count,
        expected_row_count: payload.expected_row_count,
        span_days: payload.span_days,
        peak_kw: payload.peak_kw,
        parse_status: payload.parse_status,
        flags: payload.flags,
        format_fingerprint,
        user_action: hasVerify ? 'pending' : null,
      })
      .select('id')
      .single();

    if (error) {
      console.warn('[parserTelemetry] insert failed:', error.message);
      return null;
    }
    return (data as { id: string } | null)?.id ?? null;
  } catch (err) {
    console.warn('[parserTelemetry] insert threw:', err instanceof Error ? err.message : err);
    return null;
  }
}

/**
 * Update user_action (and optionally corrected_demand_column) on an existing
 * row. Fire-and-forget; never throws.
 */
export async function updateParserAction(
  id: string,
  userAction: 'confirmed' | 'disputed',
  correctedDemandColumn?: string | null
): Promise<void> {
  try {
    const patch: Record<string, unknown> = { user_action: userAction };
    if (correctedDemandColumn !== undefined) {
      patch.corrected_demand_column = correctedDemandColumn;
    }
    const { error } = await supabase.from('parser_events').update(patch).eq('id', id);
    if (error) {
      console.warn('[parserTelemetry] update failed:', error.message);
    }
  } catch (err) {
    console.warn('[parserTelemetry] update threw:', err instanceof Error ? err.message : err);
  }
}
